import { z } from "zod";
export const DEFAULT_CRM_LEAD_SCORING_CONFIG = {
    fit: {
        decisionMaker: 25,
        seniorTitle: 15,
        owner: 10,
        linkedCompany: 10,
        segmentContext: 8,
        source: 8,
        permittedOutreach: 5,
    },
    lifecycle: {
        opportunity: 30,
        salesQualifiedLead: 28,
        marketingQualifiedLead: 22,
        lead: 15,
        customer: 12,
        default: 5,
    },
    engagement: {
        activityEvent: 12,
        activityMax: 36,
        outboundTouch: 8,
        openedEmail: 16,
        clickedEmail: 24,
        openDeal: 20,
    },
    recency: {
        lastWeek: 30,
        lastMonth: 20,
        lastQuarter: 10,
    },
    weighting: {
        fitWeight: 45,
        engagementWeight: 55,
    },
    thresholds: {
        warm: 40,
        hot: 70,
    },
    sourceBoosts: [],
};
const ScorePointsSchema = z.number().int().min(-100).max(100);
const NonNegativeScorePointsSchema = z.number().int().min(0).max(100);
export const CrmLeadScoringSourceBoostSchema = z.object({
    source: z.string().min(1).max(80),
    points: ScorePointsSchema,
});
export const CrmLeadScoringConfigSchema = z
    .object({
    fit: z
        .object({
        decisionMaker: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit.decisionMaker),
        seniorTitle: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit.seniorTitle),
        owner: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit.owner),
        linkedCompany: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit.linkedCompany),
        segmentContext: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit.segmentContext),
        source: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit.source),
        permittedOutreach: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit.permittedOutreach),
    })
        .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.fit),
    lifecycle: z
        .object({
        opportunity: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.lifecycle.opportunity),
        salesQualifiedLead: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.lifecycle.salesQualifiedLead),
        marketingQualifiedLead: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.lifecycle.marketingQualifiedLead),
        lead: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.lifecycle.lead),
        customer: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.lifecycle.customer),
        default: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.lifecycle.default),
    })
        .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.lifecycle),
    engagement: z
        .object({
        activityEvent: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.engagement.activityEvent),
        activityMax: NonNegativeScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.engagement.activityMax),
        outboundTouch: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.engagement.outboundTouch),
        openedEmail: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.engagement.openedEmail),
        clickedEmail: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.engagement.clickedEmail),
        openDeal: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.engagement.openDeal),
    })
        .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.engagement),
    recency: z
        .object({
        lastWeek: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.recency.lastWeek),
        lastMonth: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.recency.lastMonth),
        lastQuarter: ScorePointsSchema.default(DEFAULT_CRM_LEAD_SCORING_CONFIG.recency.lastQuarter),
    })
        .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.recency),
    weighting: z
        .object({
        fitWeight: z
            .number()
            .int()
            .min(0)
            .max(100)
            .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.weighting.fitWeight),
        engagementWeight: z
            .number()
            .int()
            .min(0)
            .max(100)
            .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.weighting.engagementWeight),
    })
        .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.weighting),
    thresholds: z
        .object({
        warm: z
            .number()
            .int()
            .min(0)
            .max(100)
            .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.thresholds.warm),
        hot: z
            .number()
            .int()
            .min(0)
            .max(100)
            .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.thresholds.hot),
    })
        .default(DEFAULT_CRM_LEAD_SCORING_CONFIG.thresholds),
    sourceBoosts: z.array(CrmLeadScoringSourceBoostSchema).max(20).default([]),
})
    .superRefine((config, ctx) => {
    if (config.weighting.fitWeight + config.weighting.engagementWeight !== 100) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["weighting"],
            message: "Fit and engagement weights must total 100",
        });
    }
    if (config.thresholds.hot <= config.thresholds.warm) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["thresholds", "hot"],
            message: "Hot threshold must be higher than warm threshold",
        });
    }
    const seenSources = new Set();
    for (const [index, boost] of config.sourceBoosts.entries()) {
        const key = boost.source.trim().toLowerCase();
        if (seenSources.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["sourceBoosts", index, "source"],
                message: "Source boosts must be unique",
            });
        }
        seenSources.add(key);
    }
});
export const CrmLeadScoringConfigResponseSchema = z.object({
    config: CrmLeadScoringConfigSchema,
    updatedBy: z.string().email().nullable(),
    updatedAt: z.string().datetime().nullable(),
});
//# sourceMappingURL=crm-scoring.js.map